/*
 * Copyright (c) 2025 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import React, {useEffect, useState} from "react";
import toast from "react-hot-toast";
import {usePostRequest} from "../../../../../api/helper";
import VAR_COMMON_CONSTANTS from "../../../../../constants/globalVariables";
import useGetUrlParams from "../../../../../hooks/useGetUrlParams";
import {
  EAdasProgramsLoadingStateTypes,
  selectAdasProgramsMetaData,
  selectSensingSystemRequirementsLoadingStateTypeIsLoading,
  selectSensingSystemRequirementsReportContent,
  setSensingSystemRequirementsReportContent
} from "../../../../../redux/ducks/AdasPrograms/slice";
import {useAppDispatch, useAppSelector} from "../../../../../redux/hooks";
import ArgosFlexbox from "../../../Containers/ArgosFlexbox";
import Loader from "../../../Loader/Loader";
import {CAdasProgramsRequests} from "../../../../pages/AdasPrograms/ItemDetails/types";
import {MultiPageWizard} from "../../../MultiPageWizard/MultiPageWizard";
import ADAS_PROGRAMS_CONSTANTS from "../../constants";
import ArgosButton from "../../../Buttons/ArgosButton"
import {AdasProgramsReportPages} from "../../../../pages/AdasPrograms/ItemDetails/common/constants";
import {SelectName} from "./SelectName/SelectName";
import {DialogTree} from "./DialogTree/DialogTree";
import SelectObjects from "./SelectObjects/SelectObjects";


interface IProps {
  page: AdasProgramsReportPages
  onSave: (_selectedDatasets: any) => void
  onClickClose: () => void
  dialogIsLoading?: boolean
  shouldPrepopulateWithRightClickedNode?: boolean
  isEdit?: boolean
}

const SelectSourceDatasetsDialog3 = (props: IProps) => {
  const dispatch = useAppDispatch()
  const adasProgramMetaData = useAppSelector(selectAdasProgramsMetaData)
  const adasProgramId = adasProgramMetaData?.adasProgramId

  const {page, onSave, onClickClose, dialogIsLoading} = props;
  const [datasetsByStudy, setDatasetsByStudy] = useState<any>({})
  const [disabledNodes, setDisabledNodes] = useState<any>([])
  const steps = [`Select ${VAR_COMMON_CONSTANTS.VAR_DATA_SET.toLowerCase()}`, 'Select objects', 'Select formats and names']

  /**
   * fetch all the datasets for this program
   * TODO: Could fetch report data here also.
   */
  const fetchRedlineDatasetsHandle = usePostRequest(ADAS_PROGRAMS_CONSTANTS.itemDetails.coverageRequirements.fetchFilteredDatasetsEndpoint)
  const [isLoadingFetchRedlineDatasets, setIsLoadingFetchRedlineDatasets] = useState(false)
  const [datasetIdToData, setDatasetIdToData] = useState<any>({})
  /**
   * - Fetches a tree structure including all the merged datasets for the adasProgramId.
   * - Builds a map of dataset IDs to dataset data.
   */
  const fetchRedlineDatasets = () => {
    if (!adasProgramId) return
    setIsLoadingFetchRedlineDatasets(true)
    fetchRedlineDatasetsHandle({adasProgramId, page}).then((_fetchedRedlineDatasetsByStudy) => {
      setDatasetsByStudy(_fetchedRedlineDatasetsByStudy)
      const ids = getIds(_fetchedRedlineDatasetsByStudy)
      setDatasetIdToData(ids[0])
      setDisabledNodes(ids[1])

      /* Adding disabled messsage **/
      if (ids[1].length > 0)
        toast.error((t) => (
            <span>
          One or more data sets may be disabled in tree. Please locate data set file in blob storage.
          <ArgosButton
            variant="text"
            //size="small"
            fontSize={11.5}
            onClick={() => toast.remove(t.id)}>Dismiss
          </ArgosButton>
          </span>
          ),
          {
            duration: Infinity
          })
    }).catch((err) => {
      console.log(err)
      toast.error('Error fetching selected datasets')
    }).finally(() => {
      setIsLoadingFetchRedlineDatasets(false)
    })
  }
  useEffect(() => {
    fetchRedlineDatasets()
  }, [adasProgramId, page]);

  const getIds = (_val: any) => {
    const _getIds = (tree: any, ids: any, disabledNodes: any, disabledDatasets: any) => {
      if (!tree) return
      if (tree?.children) {
        tree.children.forEach((child: any) => {
          _getIds(child, ids, disabledNodes, disabledDatasets)
        })
      }
      if (tree?.dataset) {
        ids[tree.dataset.id] = tree.dataset
        /* Storing nodeids from the retreived datasetids that erred **/
        if (disabledDatasets.includes(tree.dataset.id)) {
          disabledNodes.push(tree.nodeId)
        }
      }
    }
    const ids: any = {}
    const disabledNodes: any = []
    Object.keys(_val).forEach((studyId) => {
      _getIds(_val[studyId], ids, disabledNodes, _val[studyId]['disabledDatasets'])
    })
    return [ids, disabledNodes]
  }

  /**
   * Load report data
   */
  const reportData = useAppSelector(selectSensingSystemRequirementsReportContent)
  const {reportId} = useGetUrlParams()
  useEffect(() => {
    if (!props.isEdit) return
    fetchGenerateReportForAdasPrograms()
  }, [reportId, props.isEdit, adasProgramId]);
  const reportHandle = usePostRequest(CAdasProgramsRequests.getReport.url);
  const [getReportLoading, setGetReportLoading] = useState(false)
  const fetchGenerateReportForAdasPrograms = () => { // TODO: Do this call and other duplicates in saga
    if (reportId == null) return
    setGetReportLoading(true);
    const body = {
      reportId,
      adasProgramId
    }
    reportHandle(body)
      .then((data) => {
        dispatch(setSensingSystemRequirementsReportContent(data))
      }).catch((e) => {
      console.log(e)
      toast.error("Error loading the dataset data.")
    }).finally(() => {
      setGetReportLoading(false)
    })
  }
  const [selectedDatasetId, setSelectedDatasetId] = useState('')
  const [selectedDataset, setSelectedDataset] = useState<any>(null)
  const [selectedObjects, setSelectedObjects] = useState<any>({})
  const [preselectedObjects, setPreselectedObjects] = useState<any>({})
  const [reportName, setReportName] = useState<string>('')
  const [reportFormat, setReportFormat] = useState<string>('')
  const [selectedReports, setSelectedReports] = useState<{ [reportFormat: string]: string }>({})  /** {reportFormat : reportName} */

  /**
   * Prepopulate the dialog with the right clicked node data
   */
  useEffect(() => {
    if (!reportData || !props.shouldPrepopulateWithRightClickedNode) return
    const selectedReportData = reportData?.selectedReportData
    const _selectedObjects = selectedReportData?.selectedObjects
    const _reportName = selectedReportData?.reportName
    const _selectedDatasetId = selectedReportData?.selectedDataset?.id
    const _selectedDataset = datasetIdToData[_selectedDatasetId]
    const _reportFormat = selectedReportData?.reportFormat
    setSelectedDatasetId(_selectedDatasetId)
    setSelectedDataset(_selectedDataset)
    setSelectedObjects(_selectedObjects)
    setPreselectedObjects(_selectedObjects)
    setReportName(_reportName)
    setReportFormat(_reportFormat)
  }, [reportData, datasetIdToData, props.shouldPrepopulateWithRightClickedNode])

  const onFinish = () => {
    const selectedDataSetId = selectedDataset?.id
    const studyId = selectedDataset?.referencedBy?.[0] ?? null

    const payload = {
      selectedDataset: {id: selectedDataSetId, studyId},
      selectedObjects, /* Map {object ID: object data}. Ideally it would be {object ID: true} but AnalysisResults requires additional data */
      reportName,
      reportFormat,
      selectedReports, /* Map {reportFormat : reportName} */
      isReportSynced: false,
      reportId
    }
    onSave(payload)
  }


  const isEditLoading = useAppSelector((state) => selectSensingSystemRequirementsLoadingStateTypeIsLoading(state, EAdasProgramsLoadingStateTypes.EDIT_REPORT))
  const isCreateLoading = useAppSelector((state) => selectSensingSystemRequirementsLoadingStateTypeIsLoading(state, EAdasProgramsLoadingStateTypes.CREATE_REPORT))
  const isMainLoading = dialogIsLoading || getReportLoading || isEditLoading || isCreateLoading

  const isSelectDatasetValid = selectedDataset
  const isSelectObjectsValid = selectedObjects && Object.keys(selectedObjects).length
  const isSelectNameValid = (reportName && reportFormat) ||
    (Object.keys(selectedReports).length > 0 && Object.values(selectedReports).every((name: any) => name.length > 0))
  const isFinishDisabled = !isSelectDatasetValid || !isSelectObjectsValid || !isSelectNameValid || isMainLoading

  return (
    <>
      <ArgosFlexbox vertical style={{height: '100%', overflow: 'auto'}}>
        <ArgosFlexbox vertical style={{flex: 1, overflow: 'auto'}}>
          <Loader isLoading={isMainLoading ?? false}>
            <MultiPageWizard steps={steps} onFinish={onFinish} isNextDisabled={[!isSelectDatasetValid, !isSelectObjectsValid, isFinishDisabled]} isFinishLoading={isEditLoading || isCreateLoading}
                             onClickClose={onClickClose}>
              <Loader isLoading={isLoadingFetchRedlineDatasets || getReportLoading}>
                <DialogTree
                  sourceData={datasetsByStudy}
                  setSelectedDataset={(_selectedDatasets: any) => {
                    const keys = Object.keys(_selectedDatasets)
                    if (keys.length === 0) {
                      setSelectedDataset(null)
                    } else {
                      setSelectedDataset(_selectedDatasets?.[keys[0]])
                    }
                  }}
                  selectedDatasets={selectedDataset ? {[selectedDataset.id]: selectedDataset} : {}}
                  isMultiSelect={false}
                  disabledNodes={disabledNodes}
                />
              </Loader>
              <SelectObjects
                selectedDataset={selectedDataset}
                preselectedObjects={preselectedObjects}
                selectedObjects={selectedObjects}
                setSelectedObjects={setSelectedObjects}
              />
              <SelectName
                page={page}
                multiSelect={!props.isEdit}
                reportName={reportName}
                setReportName={setReportName}
                reportFormat={reportFormat}
                setReportFormat={setReportFormat}
                selectedReports={selectedReports}
                setSelectedReports={setSelectedReports}
                selectedDataset={selectedDataset}
                selectedObjects={selectedObjects}
              />
            </MultiPageWizard>
          </Loader>
        </ArgosFlexbox>
      </ArgosFlexbox>
    </>
  )
}


export default SelectSourceDatasetsDialog3
